import React, { useLayoutEffect, useState } from 'react';
import { AccessToken, IdentityToken, User } from '../types/auth.type';
import { useDispatch, useSelector } from 'react-redux';
import { UserState } from '../types/user.store.type';
import { logIn, sessionExpired, setEarlyCheckoutFlag, setHidePage } from '../store/actions';
import { AuthMethods, AuthUtils, SharedUtils, ValidationUtils } from '../utils';
import { usePageViewTracker } from '../hooks/usePageViewTracker';
import { useHistory } from 'react-router-dom';
import { AppRoutes } from '../routes';
import { LegacyRoutes } from '../types/legacy.type';
import { StorageKeys } from '../types/keys.type';
import ddLogger from '../logging/ddLogger';
import decode from 'jwt-decode';
import moment from 'moment';

const restrictedReferrers = ValidationUtils.validateAndGetEnvVar('REACT_APP_REFERRER_URI').split(';');

export const requiredAuth = (WrappedComponent: React.FC<any>, rest?: any) => {
    const Authentication: React.FC<any> = () => {
        const dispatch = useDispatch();
        const storeUser: any = useSelector((store: { user: UserState }) => store.user);
        const history = useHistory();
        const [urlIframe, setUrlIframe] = useState('');
        const [shouldRenderComponent, setShouldRenderComponent] = useState(false);

        usePageViewTracker();

        const handleAccessAndIdentityToken = (auth: any) => {
            const storedAccessToken = SharedUtils.getDecodedAccessToken();
            const storedIdentityToken = SharedUtils.getDecodedIdentityToken();

            if (storedAccessToken && storedIdentityToken) {
                const newIdToken = SharedUtils.decodeToken(auth.id_token) as IdentityToken;
                if (newIdToken && storedIdentityToken.email !== newIdToken.email) {
                    dispatch(sessionExpired(true));
                }
            }

            SharedUtils.setSessionStorageItem(StorageKeys.ACCESS_TOKEN, auth.access_token);
            SharedUtils.setSessionStorageItem(StorageKeys.IDENTITY_TOKEN, auth.id_token);

            // TODO why user is always null
            if (storeUser.user === null) {
                const idToken = SharedUtils.getDecodedIdentityToken() as IdentityToken;
                const currentUser: User = {
                    name: idToken.given_name,
                    email: idToken.email,
                };
                dispatch(logIn(currentUser));
            }
        };

        const shouldBeAuthenticated = () => {
            const referrer = AuthUtils.getReferrer();
            return referrer && restrictedReferrers.includes(referrer);
        };

        const handleIframeAuth = (e: any) => {
            if (e.origin !== window.location.origin || typeof e.data !== 'object' || e.data.type !== 'token_refresh') {
                return;
            }

            const { payload } = e.data;

            // TODO TRP-454 restore session expired functionality
            // if (payload.error && shouldBeAuthenticated()) {
            // AuthUtils.deleteReferrer();
            // window.location.assign(`${vacasaUrl}/my-trips`);
            // return;
            // }

            // TODO TRP-454 restore session expired functionality
            // If IDP response has an error and user was previously authenticated, the user closed his session
            // if (payload.error && AuthUtils.isAuthenticated()) {
            // dispatch(sessionExpired(true));
            // }

            if (payload.access_token && payload.id_token) {
                handleAccessAndIdentityToken(payload);
            }
        };
        /**
         *  This method was developed to ensure the continued functionality of promotional emails,
         *  which arrive at Trip Portal via legacy routes.
         *
         *  The main purpose of this component is to intercept requests coming through these legacy routes
         *  and redirect them to the new service routes, performing a "push" in the history.
         *
         */
        const handleLegacyRouting = () => {
            const { search } = window.location;
            const searchParams = new URLSearchParams(search);
            const location = searchParams.get('location');
            let destination;

            if (location) {
                switch (location) {
                    case LegacyRoutes.PARKING:
                    case LegacyRoutes.DRIVING_DIRECTIONS:
                        destination = AppRoutes.GETTING_THERE;
                        break;
                    case LegacyRoutes.PAY_BALANCE:
                        destination = AppRoutes.PAYMENT;
                        break;
                    case LegacyRoutes.CANCEL:
                        destination = AppRoutes.CANCEL;
                        break;
                    case LegacyRoutes.MODIFY:
                        destination = AppRoutes.MODIFY_TRIP;
                        break;
                    case LegacyRoutes.LATE_CHECKOUT:
                        destination = AppRoutes.MODIFY_TRIP_ADJUSTMENTS.replace(':adjustment', 'late-checkout');
                        break;
                    case LegacyRoutes.REGISTER_VEHICLE:
                        destination = AppRoutes.VEHICLE_REGISTRATION;
                        break;
                    case LegacyRoutes.SIGN_AGREEMENT:
                        destination = AppRoutes.RENTAL_AGREEMENT;
                        break;
                    case LegacyRoutes.RECEIPT:
                        destination = AppRoutes.RECEIPT;
                        break;

                    case LegacyRoutes.EARLY_CHECKOUT:
                        // The flag has been set to true to deploy a pop-up.
                        // This pop-up will provide the guest with the option to check out of the unit earlier.
                        dispatch(setEarlyCheckoutFlag(true));
                        destination = AppRoutes.RESERVATION;
                        break;
                    default:
                        destination = AppRoutes.RESERVATION;
                }

                const utmQueryParams = AuthUtils.getUTMQueryParams();

                if (utmQueryParams.length > 0) {
                    destination = `${destination}?${utmQueryParams.join('&')}`;
                }

                return history.push(destination);
            }
        };

        const enableRefreshAuthIframe = () => {
            window.removeEventListener('message', handleIframeAuth);
            window.addEventListener('message', handleIframeAuth, false);
            setUrlIframe(AuthUtils.getLoginRefreshUrl());
        };

        useLayoutEffect(() => {
            const utmQueryParams = AuthUtils.getUTMQueryParams();
            let isEmployee = !!utmQueryParams.find((item) => item === 'utm_source=resedit');
            if (!SharedUtils.getSessionStorageItem(StorageKeys.USER_TYPE)) {
                SharedUtils.setSessionStorageItem(StorageKeys.USER_TYPE, isEmployee ? 'employee' : 'guest');
            }

            if (!AuthUtils.isInsideIFrame()) {
                const auth = AuthUtils.getAuthFromUrl();
                const authFromURL = !auth
                    ? {}
                    : {
                          id_token: auth.id_token ? auth.id_token.substr(0, 15) : 'empty',
                          access_token: auth.access_token ? auth.access_token.substr(0, 15) : 'empty',
                          legacy_token: auth.legacyToken ? auth.legacyToken.substr(0, 15) : 'empty',
                          reservation_id: auth.reservationId ? auth.reservationId : 'empty',
                      };
                AuthUtils.removeUrlFragment();
                ddLogger.info(`Checking auth data`, { authFromURL });

                if (!auth) {
                    // If there's no auth in the url, we check the storage
                    const storedReservationId = SharedUtils.getSessionStorageItem(StorageKeys.RESERVATION_ID);
                    const accessToken = SharedUtils.getDecodedAccessToken();
                    const isLegacyTokenValid = AuthUtils.isLegacySessionValid();
                    const isAccessTokenValid = AuthUtils.isTokenValid(accessToken);

                    if (isLegacyTokenValid || (isAccessTokenValid && storedReservationId)) {
                        dispatch(setHidePage(false));
                        enableRefreshAuthIframe();
                        setShouldRenderComponent(true);
                        return;
                    }

                    // possible session expired, because of Safari/cookies issues
                    if (storedReservationId && AuthUtils.getAuthMethod() === AuthMethods.AUTH_RESERVATION) {
                        ddLogger.info(`User redirected to IDP to request access token`);
                        AuthUtils.goToIdpLoginNoPrompt(storedReservationId);
                        return;
                    }

                    AuthUtils.goToMyTrips();
                    return;
                }

                // if there's an authentication in the url, we clean the stored data.
                ddLogger.info('Auth found in URL, clearing all auth data', { authFromURL });

                AuthUtils.clearStoredAuthData();

                AuthUtils.storeReferrer(document.referrer);

                if (auth.reservationId) {
                    AuthUtils.goToIdpLoginNoPrompt(auth.reservationId);
                    return;
                }

                if (auth.legacyToken) {
                    SharedUtils.setSessionStorageItem(StorageKeys.LEGACY_TOKEN, auth.legacyToken);
                    AuthUtils.setAuthMethod(AuthMethods.AUTH_LEGACY);
                    setShouldRenderComponent(true);
                    enableRefreshAuthIframe();
                    dispatch(setHidePage(false));
                    handleLegacyRouting();
                    return;
                }

                const reservationId = auth.state ? AuthUtils.getReservationIdFromState(auth.state) : null;
                if (auth.access_token && auth.id_token && reservationId) {
                    SharedUtils.setSessionStorageItem(StorageKeys.ACCESS_TOKEN, auth.access_token);
                    SharedUtils.setSessionStorageItem(StorageKeys.IDENTITY_TOKEN, auth.id_token);
                    SharedUtils.setSessionStorageItem(StorageKeys.RESERVATION_ID, reservationId);
                    AuthUtils.setAuthMethod(AuthMethods.AUTH_RESERVATION);

                    const idToken = SharedUtils.getDecodedIdentityToken() as IdentityToken;
                    const currentUser: User = {
                        name: idToken.given_name,
                        email: idToken.email,
                    };

                    dispatch(logIn(currentUser));
                    setShouldRenderComponent(true);
                    enableRefreshAuthIframe();
                    dispatch(setHidePage(false));

                    const previousView = auth.state ? AuthUtils.getPreviousViewFromState(auth.state) : null;
                    if (previousView) {
                        history.push(previousView);
                    }

                    return;
                }

                AuthUtils.goToMyTripsLogin();
                return;
            } else {
                const iframeAuth = AuthUtils.getAuthFromIframeUrl();

                if (iframeAuth) {
                    let timeOut = Number(ValidationUtils.validateAndGetEnvVar('REACT_APP_TOKEN_REFRESH_TIME'));
                    if (iframeAuth.access_token) {
                        let accessToken = decode(iframeAuth.access_token) as AccessToken;
                        timeOut = moment().diff(moment.unix(accessToken.exp).subtract(60, 'seconds'), 'seconds') * -1000;
                    }

                    window.parent.postMessage({ type: 'token_refresh', payload: iframeAuth }, window.location.origin);
                    setTimeout(() => {
                        window.location.assign(AuthUtils.getLoginRefreshUrl());
                    }, timeOut);
                } else {
                    AuthUtils.goToLogin();
                }
            }

            return () => {
                window.removeEventListener('message', handleIframeAuth);
            };
        }, []);

        if (AuthUtils.isInsideIFrame()) {
            return <React.Fragment />;
        }

        return (
            <React.Fragment>
                <iframe title="token refresh" src={urlIframe} className="hidden" />
                {shouldRenderComponent && <WrappedComponent {...rest} />}
            </React.Fragment>
        );
    };

    return Authentication;
};
